/**
 * Compliance Dashboard Widget
 * Used in HmsDashboard right-sidebar to show TR128 + CO2e summary at a glance.
 */
import { Link } from 'react-router-dom';
import { ShieldCheck, Leaf, ArrowRight, Award, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

// Demo summary data — in production, this would come from the dashboard API
const TR128_SUMMARY = {
  overallPct: 85,
  level3Jobs: 4,
  level2Jobs: 2,
  level1Jobs: 1,
  topGap: 'D4 GPS (60%)',
};

const CO2E_SUMMARY = {
  thisMonthTonnes: 9.52,
  trendPct: -3,          // vs last month
  carbonTaxSGD: 428,     // @ 2026 rate $45/t
  avgIntensity: 74.8,    // gCO2e/tonne-km
};

// Re-export all sub-components so `@/components/compliance` resolves correctly
// regardless of whether TypeScript resolves to this file or compliance/index.ts
export { ComplianceField } from './compliance/ComplianceField';
export { ComplianceLegend } from './compliance/ComplianceLegend';
export { ComplianceScoreBadge } from './compliance/ComplianceScoreBadge';
export { ComplianceChecklist } from './compliance/ComplianceChecklist';
export { MilestoneMapPanel } from './compliance/MilestoneMapPanel';
export { useCompliance } from './compliance/useCompliance';
export type { ComplianceData, DomainStatus } from './compliance/useCompliance';

export function ComplianceDashboardCard() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-sky-600" />
          Compliance Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {/* TR128 Section */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-sky-500" />
              TR128 — This Week
            </span>
            <span className="text-sm font-bold text-sky-700">{TR128_SUMMARY.overallPct}%</span>
          </div>
          <Progress value={TR128_SUMMARY.overallPct} className="h-1.5 mb-2" />
          <div className="flex items-center gap-2 text-xs">
            <Badge className="bg-emerald-100 text-emerald-700 text-[9px] px-1.5 py-0">
              <Award size={9} className="mr-0.5" /> L3 ×{TR128_SUMMARY.level3Jobs}
            </Badge>
            <Badge className="bg-sky-100 text-sky-700 text-[9px] px-1.5 py-0">L2 ×{TR128_SUMMARY.level2Jobs}</Badge>
            <Badge className="bg-slate-100 text-slate-600 text-[9px] px-1.5 py-0">L1 ×{TR128_SUMMARY.level1Jobs}</Badge>
          </div>
          {TR128_SUMMARY.topGap && (
            <p className="text-[10px] text-amber-700 flex items-center gap-1 mt-1.5">
              <AlertTriangle size={10} /> Gap: {TR128_SUMMARY.topGap}
            </p>
          )}
          <Link to="/hms/compliance/tr128" className="text-[10px] text-sky-600 flex items-center gap-0.5 hover:underline mt-1">
            Full TR128 Audit <ArrowRight size={9} />
          </Link>
        </div>

        <div className="border-t" />

        {/* CO2e Section */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium flex items-center gap-1.5">
              <Leaf className="h-3 w-3 text-emerald-500" />
              CO2e — This Month
            </span>
            <span className="text-sm font-bold text-emerald-700">{CO2E_SUMMARY.thisMonthTonnes} t</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-[10px] text-muted-foreground">
            <div>
              <span className="font-medium">Carbon Tax: </span>
              <span className="text-amber-700 font-semibold">SGD ${CO2E_SUMMARY.carbonTaxSGD}</span>
            </div>
            <div>
              <span className="font-medium">Intensity: </span>
              <span>{CO2E_SUMMARY.avgIntensity} g/tkm</span>
            </div>
          </div>
          <div className="flex items-center gap-1 mt-1 text-[10px]">
            {CO2E_SUMMARY.trendPct < 0 ? (
              <span className="text-emerald-600">↓ {Math.abs(CO2E_SUMMARY.trendPct)}% vs last month</span>
            ) : (
              <span className="text-rose-600">↑ {CO2E_SUMMARY.trendPct}% vs last month</span>
            )}
          </div>
          <Link to="/hms/compliance/co2e" className="text-[10px] text-emerald-600 flex items-center gap-0.5 hover:underline mt-1">
            Full CO2e Report <ArrowRight size={9} />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
